import { useState, useCallback } from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Cell,
  PieChart,
  Pie,
  Legend,
} from 'recharts';
import DateSelector from '../components/analytics/DateSelector';
import DeviceFilter from '../components/analytics/DeviceFilter';
import StoreFilter from '../components/analytics/StoreFilter';
import CartUpsellsCard from '../components/analytics/insights/CartUpsellsCard';
import type {
  InsightsData,
  AggregateData,
  TimingStats,
  FeedbackStats,
} from '../types/analyticstypes';
import { defaultDateRange, freshenRangeIfPreset } from '../utils/analytics/dateRange';
import type { DateRange, PresetKey } from '../utils/analytics/dateRange';
import { formatSec, formatPrice, formatRate, formatLatencyMs } from '../utils/analytics/format';
import { getInsights, getAggregate } from '../services/analyticsservice';

const PAYMENT_BRAND_COLORS: Record<string, string> = {
  Visa: '#1a1f71',
  Mastercard: '#eb001b',
  Amex: '#2e77bb',
  Discover: '#ff6000',
  Dankort: '#e8090e',
  'Gift Card': '#34d399',
  Card: '#94a3b8',
  Debit: '#0ea5e9',
};

const ENTRY_MODE_COLORS: Record<string, string> = {
  T: '#22c55e',
  I: '#3b82f6',
  S: '#f59e0b',
  M: '#a855f7',
};

const END_REASON_COLORS: Record<string, string> = {
  order_complete: '#22c55e',
  timeout: '#f97316',
  cancel: '#ef4444',
  error_start_over: '#9ca3af',
};

const FUNNEL_COLORS = ['#2563eb', '#3b82f6', '#60a5fa', '#34d399', '#22c55e'];

function utcHourToLocal(utcHour: number): number {
  const now = new Date();
  const utcDate = new Date(Date.UTC(now.getFullYear(), now.getMonth(), now.getDate(), utcHour));
  return utcDate.getHours();
}

function formatHour(localHour: number): string {
  const ampm = localHour >= 12 ? 'PM' : 'AM';
  const h = localHour % 12 || 12;
  return `${h}${ampm}`;
}

function toLocalHours(
  data: { hour: number; count: number; revenue: number }[]
): { hour: number; count: number; revenue: number }[] {
  const localMap: Record<number, { count: number; revenue: number }> = {};
  for (const d of data) {
    const localHour = utcHourToLocal(d.hour);
    if (!localMap[localHour]) localMap[localHour] = { count: 0, revenue: 0 };
    localMap[localHour].count += d.count;
    localMap[localHour].revenue += d.revenue;
  }
  const result: { hour: number; count: number; revenue: number }[] = [];
  for (let h = 0; h < 24; h++) {
    result.push({
      hour: h,
      count: localMap[h]?.count || 0,
      revenue: localMap[h]?.revenue || 0,
    });
  }
  return result;
}

function rateColor(rate: number): string {
  if (rate > 50) return 'green';
  if (rate >= 20) return 'orange';
  return 'red';
}

export default function AnalyticsInsightsPage() {
  const [range, setRange] = useState<DateRange>(() => defaultDateRange());
  const [presetKey, setPresetKey] = useState<PresetKey | null>('today');
  const [storeNumber, setStoreNumber] = useState('');
  const [deviceId, setDeviceId] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<InsightsData | null>(null);
  const [aggData, setAggData] = useState<AggregateData | null>(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    const liveRange = freshenRangeIfPreset(range, presetKey);
    if (liveRange !== range) setRange(liveRange);
    try {
      const params = {
        from: liveRange.from,
        to: liveRange.to,
        storeNumber: storeNumber || undefined,
        deviceId: deviceId || undefined,
      };
      const [insightsJson, aggJson] = await Promise.all([
        getInsights(params),
        getAggregate(params),
      ]);
      setData(insightsJson);
      setAggData(aggJson);
    } catch (e) {
      const message = e instanceof Error ? e.message : 'Failed to fetch insights data';
      setError(message);
    } finally {
      setLoading(false);
    }
  }, [deviceId, range, presetKey, storeNumber]);

  const pieData = data
    ? Object.entries(data.endReasons).map(([name, value]) => ({ name, value }))
    : [];
  const pieTotal = pieData.reduce((sum, d) => sum + d.value, 0);

  const funnelData = aggData
    ? [
        { name: 'Sessions Started', value: aggData.funnel.sessions },
        { name: 'Added to Cart', value: aggData.funnel.addedToCart },
        { name: 'Checkout', value: aggData.funnel.checkoutStarted },
        { name: 'Completed', value: aggData.funnel.ordersCompleted },
      ]
    : [];

  const localHourlyData = data ? toLocalHours(data.ordersByHour) : [];

  return (
    <div>
      <h2 className="text-2xl font-bold mb-6">Insights</h2>

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
        <div className="flex flex-wrap items-end gap-4">
          <DateSelector
            value={range}
            presetKey={presetKey}
            onChange={(r, k) => {
              setRange(r);
              setPresetKey(k);
            }}
          />
          <StoreFilter value={storeNumber} onChange={setStoreNumber} />
          <DeviceFilter value={deviceId} onChange={setDeviceId} />
          <button
            onClick={loadData}
            disabled={loading}
            className="bg-blue-600 text-white px-5 py-2 rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50 transition-colors"
          >
            {loading ? 'Loading...' : 'Load'}
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl p-4 mb-6">{error}</div>
      )}

      {data && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-3 mb-6">
            <StatCard label="Sessions" value={data.totalSessions.toLocaleString()} />
            <StatCard label="Orders" value={data.completedOrders.toLocaleString()} />
            <StatCard
              label="Completion"
              value={formatRate(data.completionRate)}
              valueColor={data.completionRate > 50 ? 'text-green-600' : 'text-orange-600'}
            />
            <StatCard
              label="Paid"
              value={`${data.paidOrders} (${formatRate(data.paidRate)})`}
              valueColor={data.paidRate >= 99 ? 'text-green-600' : 'text-red-600'}
            />
            <StatCard label="Revenue" value={formatPrice(data.totalRevenue)} />
            <StatCard label="Avg Order" value={formatPrice(data.avgCartValue)} />
            <StatCard label="Items/Order" value={data.avgItemsPerOrder.toFixed(1)} />
            <StatCard label="Bag Requested" value={`${data.bagRequestedCount}`} />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
            <TimingCard title="Browse Time" subtitle="Time customer spends ordering on kiosk" stats={data.browseTime} />
            <TimingCard title="Kitchen Time" subtitle="Order submitted &rarr; Kitchen complete" stats={data.kitchenTime} />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
            {localHourlyData.length > 0 && (
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <h3 className="text-sm font-semibold text-gray-700 mb-4">Orders by Hour</h3>
                <ResponsiveContainer width="100%" height={250}>
                  <BarChart data={localHourlyData} margin={{ top: 5, right: 10, left: 10, bottom: 5 }}>
                    <XAxis dataKey="hour" tickFormatter={formatHour} tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} />
                    <Tooltip
                      formatter={(value, name) => [
                        name === 'revenue' ? formatPrice(Number(value)) : value,
                        name === 'revenue' ? 'Revenue' : 'Orders',
                      ]}
                      labelFormatter={(h) => formatHour(Number(h))}
                    />
                    <Bar dataKey="count" fill="#3b82f6" radius={[4, 4, 0, 0]} name="Orders" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}

            {aggData && (
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <h3 className="text-sm font-semibold text-gray-700 mb-4">Conversion Funnel</h3>
                <ResponsiveContainer width="100%" height={250}>
                  <BarChart data={funnelData} layout="vertical" margin={{ top: 5, right: 30, left: 100, bottom: 5 }}>
                    <XAxis type="number" />
                    <YAxis type="category" dataKey="name" width={100} tick={{ fontSize: 11 }} />
                    <Tooltip />
                    <Bar dataKey="value" radius={[0, 6, 6, 0]}>
                      {funnelData.map((_, index) => (
                        <Cell key={`cell-${index}`} fill={FUNNEL_COLORS[index] || '#2563eb'} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
                <div className="mt-3 space-y-1">
                  {funnelData.slice(0, -1).map((step, i) => {
                    const next = funnelData[i + 1];
                    if (!next) return null;
                    const rate = step.value > 0 ? ((next.value / step.value) * 100).toFixed(1) : '0';
                    return (
                      <div key={i} className="flex items-center text-xs text-gray-500">
                        <span className="w-28 truncate">{step.name}</span>
                        <span className="mx-2 text-blue-500 font-medium">&rarr; {rate}%</span>
                        <span className="truncate">{next.name}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <h3 className="text-sm font-semibold text-gray-700 mb-4">Session End Reasons</h3>
              {pieData.length > 0 ? (
                <div className="flex flex-col items-center gap-4">
                  <ResponsiveContainer width="100%" height={250}>
                    <PieChart>
                      <Pie
                        data={pieData}
                        cx="50%"
                        cy="50%"
                        outerRadius={90}
                        dataKey="value"
                        label={({ name, percent }: { name?: string; percent?: number }) =>
                          `${name ?? ''} (${((percent ?? 0) * 100).toFixed(0)}%)`
                        }
                        labelLine
                      >
                        {pieData.map((entry) => (
                          <Cell key={entry.name} fill={END_REASON_COLORS[entry.name] || '#9ca3af'} />
                        ))}
                      </Pie>
                      <Legend />
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                  <div className="space-y-1 w-full">
                    {pieData.map((entry) => {
                      const pct = pieTotal > 0 ? ((entry.value / pieTotal) * 100).toFixed(1) : '0';
                      return (
                        <div key={entry.name} className="flex items-center gap-2 text-sm">
                          <span
                            className="w-3 h-3 rounded-full inline-block"
                            style={{ backgroundColor: END_REASON_COLORS[entry.name] || '#9ca3af' }}
                          />
                          <span className="text-gray-700">{entry.name}</span>
                          <span className="text-gray-400 ml-auto">{entry.value} ({pct}%)</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ) : (
                <p className="text-gray-400 text-sm">No data</p>
              )}
            </div>

            {localHourlyData.length > 0 && (
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <h3 className="text-sm font-semibold text-gray-700 mb-4">Revenue by Hour</h3>
                <ResponsiveContainer width="100%" height={250}>
                  <BarChart data={localHourlyData} margin={{ top: 5, right: 10, left: 10, bottom: 5 }}>
                    <XAxis dataKey="hour" tickFormatter={formatHour} tick={{ fontSize: 11 }} />
                    <YAxis tickFormatter={(v) => `$${v}`} tick={{ fontSize: 11 }} />
                    <Tooltip
                      formatter={(value) => [formatPrice(Number(value)), 'Revenue']}
                      labelFormatter={(h) => formatHour(Number(h))}
                    />
                    <Bar dataKey="revenue" fill="#22c55e" radius={[4, 4, 0, 0]} name="Revenue" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
            <div className="lg:col-span-2">
              <CartUpsellsCard data={data.cartUpsells} />
            </div>
            <RankedList title="Most Ordered Items" items={data.mostOrderedItems} color="green" />
          </div>

          <div className="mb-3">
            <h3 className="text-lg font-semibold text-gray-800">Item Affinities</h3>
            <p className="text-xs text-gray-500 mt-0.5">Top co-purchases for the most ordered items</p>
          </div>
          {data.itemAffinities.length === 0 ? (
            <p className="text-gray-400 text-sm mb-6">No affinity data available</p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4 mb-6">
              {data.itemAffinities.slice(0, 10).map((affinity, i) => (
                <div key={i} className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                  <h4 className="text-sm font-semibold text-gray-700 mb-1 truncate" title={affinity.itemName}>
                    {affinity.itemName}
                  </h4>
                  <p className="text-xs text-gray-400 mb-4">Frequently bought with</p>
                  {affinity.affinities.length === 0 ? (
                    <p className="text-gray-400 text-sm">No co-purchases</p>
                  ) : (
                    <div className="space-y-3">
                      {(() => {
                        const maxCount = affinity.affinities[0]?.count || 1;
                        return affinity.affinities.map((a, j) => (
                          <div key={j}>
                            <div className="flex items-center justify-between text-xs mb-1">
                              <span className="font-medium text-gray-800 truncate mr-2" title={a.name}>{a.name}</span>
                              <span className="text-gray-500">{a.count}</span>
                            </div>
                            <div className="h-1.5 rounded-full bg-indigo-100 w-full">
                              <div
                                className="h-1.5 rounded-full bg-indigo-500"
                                style={{ width: `${(a.count / maxCount) * 100}%` }}
                              />
                            </div>
                          </div>
                        ));
                      })()}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          <h3 className="text-lg font-semibold text-gray-800 mb-3">Customer Feedback</h3>
          <FeedbackCard feedback={data.feedback} />

          <h3 className="text-lg font-semibold text-gray-800 mb-3">Loyalty</h3>
          <p className="text-xs text-gray-400 -mt-2 mb-3">Of completed orders ({data.completedOrders})</p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <StatCard
              label="Loyalty Customers"
              value={`${data.loyaltyCustomerCount} (${formatRate(data.loyaltyCustomerRate)})`}
              valueColor={data.loyaltyCustomerCount > 0 ? 'text-purple-700' : undefined}
            />
            <StatCard
              label="Enroll Attempts"
              value={`${data.loyaltyEnrollAttemptedCount} (${formatRate(data.loyaltyEnrollAttemptedRate)})`}
            />
          </div>

          <h3 className="text-lg font-semibold text-gray-800 mb-3">Payments &amp; Behavior</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
            <StatCard
              label="Avg Pay → Approval"
              value={data.avgTimeToPaymentApprovedMs ? formatLatencyMs(data.avgTimeToPaymentApprovedMs) : '—'}
            />
            <StatCard label="Tender Approval Rate" value={formatRate(data.tenderApprovalRate)} valueColor="text-green-600" />
            <StatCard label="Tenders Approved" value={String(data.tenderApprovedCount)} />
            <StatCard
              label="Tenders Declined"
              value={String(data.tenderDeclinedCount)}
              valueColor={data.tenderDeclinedCount > 0 ? 'text-red-600' : undefined}
            />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <h3 className="text-sm font-semibold text-gray-700 mb-1">Payment Method Mix</h3>
              <p className="text-xs text-gray-400 mb-4">Approved tenders by card brand</p>
              {data.paymentMethodMix.length === 0 ? (
                <p className="text-gray-400 text-sm">No payments</p>
              ) : (
                <div className="h-56">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={data.paymentMethodMix}
                        dataKey="count"
                        nameKey="brand"
                        cx="50%"
                        cy="50%"
                        innerRadius={40}
                        outerRadius={80}
                        paddingAngle={2}
                      >
                        {data.paymentMethodMix.map((d) => (
                          <Cell key={d.brand} fill={PAYMENT_BRAND_COLORS[d.brand] || '#94a3b8'} />
                        ))}
                      </Pie>
                      <Tooltip
                        formatter={(v, _n, p) => {
                          const payload = (p as { payload?: { amount?: number } })?.payload;
                          const amount = payload?.amount ?? 0;
                          return [`${v} (${formatPrice(amount)})`, 'tenders'];
                        }}
                      />
                      <Legend iconSize={10} wrapperStyle={{ fontSize: '11px' }} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              )}
            </div>

            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <h3 className="text-sm font-semibold text-gray-700 mb-1">Card Entry Mode</h3>
              <p className="text-xs text-gray-400 mb-4">How customers presented their card</p>
              {data.entryModeMix.length === 0 ? (
                <p className="text-gray-400 text-sm">No data</p>
              ) : (
                <div className="space-y-2">
                  {(() => {
                    const total = data.entryModeMix.reduce((s, e) => s + e.count, 0) || 1;
                    return data.entryModeMix.map((e) => {
                      const pct = (e.count / total) * 100;
                      const color = ENTRY_MODE_COLORS[e.mode] || '#94a3b8';
                      return (
                        <div key={e.mode}>
                          <div className="flex items-center justify-between text-xs mb-0.5">
                            <span className="font-medium text-gray-700">{e.label}</span>
                            <span className="text-gray-500">
                              {e.count} · {formatRate(pct)}
                            </span>
                          </div>
                          <div className="bg-gray-100 rounded-full h-2">
                            <div
                              className="h-2 rounded-full"
                              style={{ width: `${pct}%`, background: color }}
                            />
                          </div>
                        </div>
                      );
                    });
                  })()}
                </div>
              )}
            </div>

            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <h3 className="text-sm font-semibold text-gray-700 mb-1">Cancellation Pattern</h3>
              <p className="text-xs text-gray-400 mb-4">Abandoned sessions by cart state</p>
              {(() => {
                const before = data.cancellations.beforeItems;
                const after = data.cancellations.afterItems;
                const total = before + after;
                if (total === 0) {
                  return <p className="text-gray-400 text-sm">No cancellations</p>;
                }
                return (
                  <div className="space-y-3">
                    <div>
                      <div className="flex items-center justify-between text-xs mb-0.5">
                        <span className="font-medium text-gray-700">Cancelled before adding items</span>
                        <span className="text-gray-500">
                          {before} · {formatRate((before / total) * 100)}
                        </span>
                      </div>
                      <div className="bg-gray-100 rounded-full h-3">
                        <div className="bg-orange-500 h-3 rounded-full" style={{ width: `${(before / total) * 100}%` }} />
                      </div>
                    </div>
                    <div>
                      <div className="flex items-center justify-between text-xs mb-0.5">
                        <span className="font-medium text-gray-700">Cancelled with items in cart</span>
                        <span className="text-gray-500">
                          {after} · {formatRate((after / total) * 100)}
                        </span>
                      </div>
                      <div className="bg-gray-100 rounded-full h-3">
                        <div className="bg-red-500 h-3 rounded-full" style={{ width: `${(after / total) * 100}%` }} />
                      </div>
                    </div>
                  </div>
                );
              })()}
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <h3 className="text-sm font-semibold text-gray-700 mb-1">Add-on Funnel</h3>
              <p className="text-xs text-gray-400 mb-4">Add-on modals shown vs accepted</p>
              {data.addOnFunnel.shown === 0 ? (
                <p className="text-gray-400 text-sm">No add-on prompts</p>
              ) : (
                <>
                  <div className="space-y-2">
                    <div>
                      <div className="flex justify-between text-xs mb-0.5">
                        <span className="text-gray-600">Shown</span>
                        <span className="font-semibold text-gray-800">{data.addOnFunnel.shown}</span>
                      </div>
                      <div className="bg-emerald-100 rounded-full h-3">
                        <div className="bg-emerald-500 h-3 rounded-full" style={{ width: '100%' }} />
                      </div>
                    </div>
                    <div>
                      <div className="flex justify-between text-xs mb-0.5">
                        <span className="text-gray-600">Accepted</span>
                        <span className="font-semibold text-gray-800">{data.addOnFunnel.accepted}</span>
                      </div>
                      <div className="bg-emerald-100 rounded-full h-3">
                        <div
                          className="bg-emerald-600 h-3 rounded-full"
                          style={{
                            width: `${data.addOnFunnel.shown > 0 ? (data.addOnFunnel.accepted / data.addOnFunnel.shown) * 100 : 0}%`,
                          }}
                        />
                      </div>
                    </div>
                  </div>
                  <p className="text-xs text-gray-500 mt-3">
                    Acceptance rate:{' '}
                    <span className="font-bold text-emerald-700">{formatRate(data.addOnFunnel.rate)}</span>
                  </p>
                </>
              )}
            </div>

            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <h3 className="text-sm font-semibold text-gray-700 mb-1">Upsell Funnel</h3>
              <p className="text-xs text-gray-400 mb-4">Impression → accept, split by source</p>
              {data.upsellFunnel.length === 0 ? (
                <p className="text-gray-400 text-sm">No upsell impressions</p>
              ) : (
                <div className="space-y-3">
                  {data.upsellFunnel.map((u) => (
                    <div key={u.source}>
                      <div className="flex items-center justify-between text-xs mb-0.5">
                        <span className="font-medium text-gray-700 capitalize">{u.source}</span>
                        <span className="text-gray-500">
                          {u.accepts}/{u.impressions} · {formatRate(u.rate)}
                        </span>
                      </div>
                      <div className="bg-fuchsia-100 rounded-full h-2">
                        <div
                          className="bg-fuchsia-500 h-2 rounded-full"
                          style={{ width: `${u.impressions > 0 ? (u.accepts / u.impressions) * 100 : 0}%` }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <h3 className="text-sm font-semibold text-gray-700 mb-1">Age Verification</h3>
              <p className="text-xs text-gray-400 mb-4">Auto vs operator approval</p>
              {data.ageVerify.total === 0 ? (
                <p className="text-gray-400 text-sm">No age checks</p>
              ) : (
                <div className="h-44">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={[
                          { name: 'Auto-approved', value: data.ageVerify.autoApproved, color: '#3b82f6' },
                          { name: 'Operator-approved', value: data.ageVerify.operatorApproved, color: '#22c55e' },
                          { name: 'Denied', value: data.ageVerify.denied, color: '#ef4444' },
                        ].filter((d) => d.value > 0)}
                        dataKey="value"
                        nameKey="name"
                        cx="50%"
                        cy="50%"
                        innerRadius={35}
                        outerRadius={70}
                      >
                        {[{ color: '#3b82f6' }, { color: '#22c55e' }, { color: '#ef4444' }].map((d, i) => (
                          <Cell key={i} fill={d.color} />
                        ))}
                      </Pie>
                      <Tooltip />
                      <Legend iconSize={10} wrapperStyle={{ fontSize: '11px' }} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              )}
              <p className="text-[11px] text-gray-500 mt-2 text-center">
                {data.ageVerify.total} total · {data.ageVerify.autoApproved} auto · {data.ageVerify.operatorApproved} operator · {data.ageVerify.denied} denied
              </p>
            </div>
          </div>



          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
            <RankedList title="Top Categories" items={data.topCategories} color="blue" />
            <RankedList title="Most Browsed Subcategories" items={data.subcategoriesBrowsed} color="purple" />

            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <h3 className="text-sm font-semibold text-gray-700 mb-4">Subcategory &rarr; Order Conversion</h3>
              {data.subcategoryConversion.length === 0 ? (
                <p className="text-gray-400 text-sm">No data</p>
              ) : (
                <div className="space-y-3">
                  {data.subcategoryConversion.map((item, i) => {
                    const color = rateColor(item.rate);
                    const bgClass =
                      color === 'green' ? 'bg-green-200' : color === 'orange' ? 'bg-orange-200' : 'bg-red-200';
                    const fillClass =
                      color === 'green' ? 'bg-green-500' : color === 'orange' ? 'bg-orange-500' : 'bg-red-500';
                    return (
                      <div key={i}>
                        <div className="flex items-center justify-between text-sm mb-1">
                          <span className="font-medium text-gray-800 truncate mr-2">{item.name}</span>
                          <span className="text-gray-500 text-xs whitespace-nowrap">
                            {item.views} views / {item.orders} orders / {formatRate(item.rate)}
                          </span>
                        </div>
                        <div className={`h-2 rounded-full ${bgClass} w-full`}>
                          <div
                            className={`h-2 rounded-full ${fillClass}`}
                            style={{ width: `${Math.min(item.rate, 100)}%` }}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </>
      )}

      {!loading && !data && !error && (
        <div className="text-center py-12 text-gray-400">
          <p className="text-lg">No data loaded</p>
          <p className="text-sm mt-1">Select a time range and click Load to view insights</p>
        </div>
      )}
    </div>
  );
}

function StatCard({ label, value, valueColor }: { label: string; value: string; valueColor?: string }) {
  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
      <p className="text-xs text-gray-500 mb-1">{label}</p>
      <p className={`text-xl font-bold ${valueColor || 'text-gray-900'}`}>{value}</p>
    </div>
  );
}

function TimingCard({ title, subtitle, stats }: { title: string; subtitle: string; stats: TimingStats }) {
  if (stats.count === 0) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <h3 className="text-sm font-semibold text-gray-700">{title}</h3>
        <p className="text-xs text-gray-400 mt-0.5" dangerouslySetInnerHTML={{ __html: subtitle }} />
        <p className="text-gray-400 text-sm mt-4">No data</p>
      </div>
    );
  }
  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
      <h3 className="text-sm font-semibold text-gray-700">{title}</h3>
      <p className="text-xs text-gray-400 mt-0.5" dangerouslySetInnerHTML={{ __html: subtitle }} />
      <div className="mt-4 grid grid-cols-4 gap-3">
        <div>
          <p className="text-xs text-gray-400">Avg</p>
          <p className="text-lg font-bold text-gray-900">{formatSec(stats.avg)}</p>
        </div>
        <div>
          <p className="text-xs text-gray-400">Median</p>
          <p className="text-lg font-bold text-gray-900">{formatSec(stats.median)}</p>
        </div>
        <div>
          <p className="text-xs text-gray-400">Min</p>
          <p className="text-lg font-bold text-green-600">{formatSec(stats.min)}</p>
        </div>
        <div>
          <p className="text-xs text-gray-400">Max</p>
          <p className="text-lg font-bold text-red-600">{formatSec(stats.max)}</p>
        </div>
      </div>
      <p className="text-xs text-gray-400 mt-2">{stats.count} transactions</p>
    </div>
  );
}

function FeedbackCard({ feedback }: { feedback: FeedbackStats }) {
  const {
    averageRatio,
    predominantMax,
    mixedMax,
    completedOrders,
    ratedCount,
    responseRate,
    top,
    mid,
    low,
    none,
  } = feedback;
  const avgDisplay = averageRatio != null ? (averageRatio * predominantMax).toFixed(1) : '—';
  const total = top + mid + low + none;

  function bar(count: number, color: string, label: string) {
    const pct = total > 0 ? (count / total) * 100 : 0;
    return (
      <div>
        <div className="flex items-center justify-between text-xs mb-0.5">
          <span className="font-medium text-gray-700">{label}</span>
          <span className="text-gray-500">
            {count} · {formatRate(pct)}
          </span>
        </div>
        <div className="bg-gray-100 rounded-full h-2.5">
          <div className={`h-2.5 rounded-full ${color}`} style={{ width: `${pct}%` }} />
        </div>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <h3 className="text-sm font-semibold text-gray-700 mb-1">Average Rating</h3>
        <p className="text-xs text-gray-400 mb-4">
          Weighted across {ratedCount} {ratedCount === 1 ? 'rating' : 'ratings'}
        </p>
        {averageRatio != null ? (
          <>
            <div className="flex items-baseline gap-1">
              <p className="text-4xl font-bold text-gray-900">{avgDisplay}</p>
              <p className="text-lg text-gray-400 font-medium">/ {predominantMax}</p>
            </div>
            <p className="text-xs text-gray-500 mt-2">{formatRate(averageRatio * 100)} of max</p>
            {mixedMax && (
              <p className="mt-2 text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1">
                ⚠ Mixed rating scales in this range (e.g. 3-scale and 5-scale). Average is normalized.
              </p>
            )}
          </>
        ) : (
          <p className="text-gray-400 text-sm mt-4">No ratings collected</p>
        )}
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <h3 className="text-sm font-semibold text-gray-700 mb-1">Response Rate</h3>
        <p className="text-xs text-gray-400 mb-4">Completed orders that left feedback</p>
        <p className="text-4xl font-bold text-gray-900">{formatRate(responseRate)}</p>
        <p className="text-xs text-gray-500 mt-2">
          {ratedCount} of {completedOrders} completed
        </p>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <h3 className="text-sm font-semibold text-gray-700 mb-1">Rating Distribution</h3>
        <p className="text-xs text-gray-400 mb-4">Top = full score · Low = below 40%</p>
        {completedOrders === 0 ? (
          <p className="text-gray-400 text-sm">No completed orders</p>
        ) : (
          <div className="space-y-2">
            {bar(top, 'bg-green-500', 'Top')}
            {bar(mid, 'bg-amber-500', 'Mid')}
            {bar(low, 'bg-red-500', 'Low')}
            {bar(none, 'bg-gray-300', 'No feedback')}
          </div>
        )}
      </div>
    </div>
  );
}

function RankedList({
  title,
  items,
  color,
}: {
  title: string;
  items: { name: string; count: number }[];
  color: 'blue' | 'green' | 'purple';
}) {
  const maxCount = items.length > 0 ? items[0].count : 1;
  const bgClass = color === 'blue' ? 'bg-blue-200' : color === 'green' ? 'bg-green-200' : 'bg-purple-200';
  const fillClass = color === 'blue' ? 'bg-blue-500' : color === 'green' ? 'bg-green-500' : 'bg-purple-500';

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
      <h3 className="text-sm font-semibold text-gray-700 mb-4">{title}</h3>
      {items.length === 0 ? (
        <p className="text-gray-400 text-sm">No data</p>
      ) : (
        <div className="space-y-3">
          {items.map((item, i) => (
            <div key={i}>
              <div className="flex items-center justify-between text-sm mb-1">
                <span className="font-medium text-gray-800 truncate mr-2">
                  {i + 1}. {item.name}
                </span>
                <span className="text-gray-500 text-xs">{item.count}</span>
              </div>
              <div className={`h-2 rounded-full ${bgClass} w-full`}>
                <div
                  className={`h-2 rounded-full ${fillClass}`}
                  style={{ width: `${(item.count / maxCount) * 100}%` }}
                />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
